import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader, AlertCircle, Settings2, Trash2 } from 'lucide-react';
import { useOnClickOutside } from '../../hooks/useOnClickOutside';
import { eventTeamApiService } from '../../services/eventTeamApi';
import type { EventPrefixRequirement, Prefix } from '../../types/eventTeam';
import { PrefixBadge } from './PrefixBadge';

export interface PrefixRequirementModalProps {
  isOpen: boolean;
  prefix: Prefix;
  requirement: EventPrefixRequirement | null;
  onClose: () => void;
  onSuccess: () => void;
}

/**
 * Admin-only requirements overlay editor (guide §1.8): points threshold (eligibility)
 * and points cost (optional spend on grant). Writes to the overlay table only —
 * the live `prefix` catalog is never modified (guide §7.4).
 */
export function PrefixRequirementModal({
  isOpen,
  prefix,
  requirement,
  onClose,
  onSuccess,
}: PrefixRequirementModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(modalRef, onClose);

  const [threshold, setThreshold] = useState('');
  const [cost, setCost] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setThreshold(requirement?.points_threshold != null ? String(requirement.points_threshold) : '');
    setCost(requirement?.points_cost != null ? String(requirement.points_cost) : '');
    setNotes(requirement?.notes ?? '');
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await eventTeamApiService.upsertPrefixRequirement(prefix.id, {
        points_threshold: threshold.trim() !== '' ? Number(threshold) : null,
        points_cost: cost.trim() !== '' ? Number(cost) : null,
        notes: notes.trim() || null,
      });
      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save requirement');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm('Remove the point requirement overlay for this prefix?')) return;
    setDeleting(true);
    setError(null);
    try {
      await eventTeamApiService.deletePrefixRequirement(prefix.id);
      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete requirement');
    } finally {
      setDeleting(false);
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="modal-overlay z-[9999]">
      <div
        ref={modalRef}
        className="modal-panel max-w-md"
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 text-glass-400 hover:text-white p-2"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        <h2 className="text-lg font-semibold tracking-tight text-white mb-1 flex items-center gap-2">
          <Settings2 className="w-6 h-6 text-accent-400" />
          Point requirements
        </h2>
        <div className="mb-4">
          <PrefixBadge prefixJson={prefix.prefix_json} name={prefix.name} size="md" />
        </div>
        {error && (
          <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20 mb-4">
            <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
            <span className="text-sm text-red-300">{error}</span>
          </div>
        )}

        <form onSubmit={handleSave} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm text-glass-300 mb-1 block">Points threshold</label>
              <input
                type="number"
                min={0}
                value={threshold}
                onChange={(e) => setThreshold(e.target.value)}
                className="glass-input w-full text-sm"
                placeholder="e.g. 100"
              />
            </div>
            <div>
              <label className="text-sm text-glass-300 mb-1 block">Points cost</label>
              <input
                type="number"
                min={0}
                value={cost}
                onChange={(e) => setCost(e.target.value)}
                className="glass-input w-full text-sm"
                placeholder="e.g. 30"
              />
            </div>
          </div>

          <div>
            <label className="text-sm text-glass-300 mb-1 block">Notes</label>
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="glass-input w-full text-sm"
            />
          </div>

          <div className="flex justify-between gap-2 pt-2">
            {requirement ? (
              <button
                type="button"
                onClick={() => void handleDelete()}
                disabled={deleting}
                className="glass-button-secondary text-sm text-red-300 inline-flex items-center gap-1.5"
              >
                {deleting ? <Loader className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                Remove overlay
              </button>
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              <button type="button" onClick={onClose} className="glass-button-secondary text-sm">
                Cancel
              </button>
              <button type="submit" disabled={submitting} className="glass-button text-sm inline-flex items-center gap-1.5">
                {submitting && <Loader className="w-4 h-4 animate-spin" />}
                Save
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
